import { type JSX, useEffect, useRef, useState } from "react";

import { Button } from "@frontend/components/atoms/Button";
import { Icon } from "@frontend/components/atoms/Icon";

import type { CopyButtonProps } from "./CopyButton.types";
import { COPY_FEEDBACK_MS } from "./constants";

export function CopyButton({
  value,
  label = "Copy",
  copiedLabel = "Copied",
  feedbackMs = COPY_FEEDBACK_MS,
  onCopy,
  variant = "neutral",
  size = "sm",
  disabled,
  className,
  children,
}: CopyButtonProps): JSX.Element {
  const [copied, setCopied] = useState(false);
  const timeout = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(timeout.current), []);

  async function handleClick() {
    const text = typeof value === "function" ? await value() : value;

    try {
      await navigator.clipboard?.writeText(text);
    } catch {
      return;
    }

    onCopy?.(text);
    setCopied(true);
    clearTimeout(timeout.current);
    timeout.current = setTimeout(() => setCopied(false), feedbackMs);
  }

  return (
    <Button
      data-slot="copy-button"
      variant={variant}
      size={size}
      aria-label={children ? undefined : copied ? copiedLabel : label}
      disabled={disabled}
      className={className}
      onClick={() => void handleClick()}
    >
      <Icon name={copied ? "Check" : "Copy"} size={size} />
      {children}
    </Button>
  );
}
